import React, { useEffect, useState } from 'react';
import { api, endpoints } from '../config/api';
import { Alert, Spinner, Badge, Button } from '../components/ui';
import DashboardCard from '../components/DashboardCard';
import AddinUsage from '../components/AddinUsage';
import { describeDeliveryOutcome, deliveryStatus } from '../constants/deliveryOutcomes';
import { ReactComponent as SuccessIcon } from '../icons/success-circle.svg';
import { ReactComponent as ErrorIcon } from '../icons/error-circle.svg';
import { ReactComponent as DeliveryIcon } from '../icons/delivery.svg';
import { ReactComponent as UserIcon } from '../icons/user.svg';
import { useT } from '../i18n';
import './DeliveryLog.css';

const messages = {
  // SignatureDeliveryLog.client values (backend: signatures/views.py::_log_delivery).
  clients: {
    tr: { outlook: 'Outlook', outlook_mobile: 'Outlook Mobil', gmail: 'Gmail' },
    en: { outlook: 'Outlook', outlook_mobile: 'Outlook Mobile', gmail: 'Gmail' },
  },
  docTitle: { tr: 'Teslimat Günlüğü - Signature Manager', en: 'Delivery Log - Signature Manager' },
  noDeliveries: { tr: 'Son 30 günde teslimat yok.', en: 'No deliveries in the last 30 days.' },
  barTitle: {
    tr: '{date}: {ok} başarılı, {unassigned} şablon atanmamış, {fail} başarısız',
    en: '{date}: {ok} successful, {unassigned} no template assigned, {fail} failed',
  },
  legendSuccess: { tr: 'İmza eklendi', en: 'Signature added' },
  legendUnassigned: { tr: 'Şablon atanmamış', en: 'No template assigned' },
  legendFailure: { tr: 'Hata', en: 'Error' },
  chartSummary: {
    tr: 'Günde ortalama {avg} imzalı e-posta · %{rate} başarı',
    en: '{avg} signed emails a day on average · {rate}% success',
  },
  tipSuccess: { tr: '{n} imza eklendi ({people} kişi)', en: '{n} signatures added ({people} people)' },
  tipUnassigned: { tr: '{n} deneme şablon atanmamış kişiden', en: '{n} attempts from people with no template' },
  tipFailure: { tr: '{n} deneme hata nedeniyle imzasız kaldı', en: '{n} attempts got no signature due to an error' },
  tipRate: { tr: 'Başarı oranı %{n}', en: '{n}% success rate' },
  tipNone: { tr: 'Bu gün teslimat yok', en: 'No deliveries this day' },
  today: { tr: 'Bugün', en: 'Today' },
  title: { tr: 'Teslimat Günlüğü', en: 'Delivery Log' },
  addinUsage: { tr: 'Eklenti Kullanımı (Son 30 Gün)', en: 'Add-in Usage (Last 30 Days)' },
  hint: {
    tr: () => <>Outlook/Gmail eklentisine gerçekten ulaşan her imza teslim denemesinin kaydı — başarılı ya da başarısız, hangi kullanıcı, hangi sebep. <strong>Önemli sınır:</strong> Outlook'un eklentiyi hiç çalıştırmadan "kullanılamıyor" diyerek gönderimi engellediği durumlar (istek sunucumuza hiç ulaşmaz) bu günlükte görünmez — bu sadece sunucumuza gelen istekleri kaydeder. Kayıtlar 90 gün sonra otomatik silinir.</>,
    en: () => <>A record of every signature delivery attempt that actually reached the Outlook/Gmail add-in — successful or not, which user, which reason. <strong>Important limit:</strong> cases where Outlook blocks sending as “unavailable” without ever running the add-in (the request never reaches our server) don’t appear here — this only records requests that reach our server. Records are deleted automatically after 90 days.</>,
  },
  error: { tr: 'Hata: {msg}', en: 'Error: {msg}' },
  allTime: { tr: 'Tüm Zamanlar', en: 'All Time' },
  allTimeHint: {
    tr: 'Bu sayılar hiç silinmez — 90 günlük saklama süresi yalnızca aşağıdaki detaylı kayıtlar (e-posta adresi içeren satırlar) için geçerlidir.',
    en: 'These totals are never deleted — the 90-day retention only applies to the detailed records below (rows containing email addresses).',
  },
  lifetimeSuccess: { tr: 'Bugüne Kadar Eklenen İmza', en: 'Signatures Added to Date' },
  lifetimeFailure: { tr: 'Bugüne Kadar Başarısız Deneme', en: 'Failed Attempts to Date' },
  last30: { tr: 'Son 30 Gün', en: 'Last 30 Days' },
  total30: { tr: 'Toplam Teslimat (30 gün)', en: 'Total Deliveries (30 days)' },
  success: { tr: 'Başarılı', en: 'Successful' },
  unassigned: { tr: 'Şablon Atanmamış', en: 'No Template Assigned' },
  unassignedBadge: { tr: 'Şablon atanmamış', en: 'No template assigned' },
  failure: { tr: 'Başarısız', en: 'Failed' },
  last30Hint: {
    tr: 'Şablonu atanmamış kişilerin istekleri hata sayılmaz: eklenti bu kişilerin maillerine dokunmaz. Başarı oranı yalnızca imza eklenmesi gereken denemeler üzerinden hesaplanır.',
    en: 'Requests from people with no template assigned aren’t errors: the add-in leaves their mail alone. The success rate only counts attempts that should have got a signature.',
  },
  clientSig: { tr: 'Outlook\'un Kendi İmzası (Son 30 Gün)', en: 'Outlook’s Own Signature (Last 30 Days)' },
  clientSigHint: {
    tr: 'Outlook ayarlarında kendi imzası açık olan kullanıcılarda Outlook\'un imzası bizimkinin yanında görünebilir. Eklenti bu ayarı yalnızca okur, değiştirmez.',
    en: 'For users with their own signature turned on in Outlook settings, Outlook’s signature may appear next to ours. The add-in only reads this setting; it never changes it.',
  },
  clientSigOn: { tr: 'Kendi İmzası Açık Kullanıcı', en: 'Users with Own Signature On' },
  clientSigOff: { tr: 'Kendi İmzası Kapalı Kullanıcı', en: 'Users with Own Signature Off' },
  daily: { tr: 'Günlük Teslimat (Son 30 Gün)', en: 'Daily Deliveries (Last 30 Days)' },
  distribution: { tr: 'Sonuç Dağılımı', en: 'Outcome Distribution' },
  records: { tr: 'Kayıtlar', en: 'Records' },
  searchPlaceholder: { tr: 'E-posta ile ara (tam eşleşme)', en: 'Search by email (exact match)' },
  clear: { tr: 'Temizle', en: 'Clear' },
  client: { tr: 'İstemci', en: 'Client' },
  outcome: { tr: 'Sonuç', en: 'Outcome' },
  groupTemplate: { tr: 'Grup / Şablon', en: 'Group / Template' },
  detail: { tr: 'Detay', en: 'Details' },
  noRecords: { tr: 'Kayıt bulunamadı.', en: 'No records found.' },
  prev: { tr: '‹ Önceki', en: '‹ Previous' },
  next: { tr: 'Sonraki ›', en: 'Next ›' },
};

// CSS-only stacked bar chart (no charting library in this app): signed
// attempts at the bottom, then "no template assigned", then errors, on a
// labelled y-axis with a hover/tap tooltip. The backend sends all 30 days,
// empty ones included, in the org's local time.
const niceCeil = (n) => {
  if (n <= 5) return 5;
  const step = 10 ** Math.floor(Math.log10(n));
  return [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].map(m => m * step).find(v => v >= n);
};
const parseDay = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const dayTotal = (d) => d.success + (d.unassigned || 0) + d.failure;
// Success rate over attempts that should have been signed — "no template
// assigned" isn't a miss.
const successRate = (ok, fail) => Math.round((ok / Math.max(ok + fail, 1)) * 100);

function DailyDeliveryChart({ series }) {
  const { t, formatDate, formatNumber } = useT(messages);
  const [active, setActive] = useState(null);
  if (!series || series.every(d => dayTotal(d) === 0)) {
    return <p className="delivery-log-empty">{t('noDeliveries')}</p>;
  }
  const top = niceCeil(Math.max(...series.map(dayTotal)));
  const ticks = [top, top / 2, 0];
  const totals = series.reduce((acc, d) => ({ ok: acc.ok + d.success, fail: acc.fail + d.failure }), { ok: 0, fail: 0 });
  const activeDays = series.filter(d => dayTotal(d) > 0).length || 1;
  const last = series.length - 1;
  const day = active === null ? null : series[active];
  const segments = [['success', 'success'], ['unassigned', 'unassigned'], ['failure', 'failure']];

  return (
    <div className="delivery-log-chart">
      <div className="delivery-log-chart-head">
        <div className="delivery-log-chart-legend">
          <span><i className="delivery-log-dot delivery-log-dot-success" />{t('legendSuccess')}</span>
          <span><i className="delivery-log-dot delivery-log-dot-unassigned" />{t('legendUnassigned')}</span>
          <span><i className="delivery-log-dot delivery-log-dot-failure" />{t('legendFailure')}</span>
        </div>
        <div className="delivery-log-chart-summary">
          {t('chartSummary', {
            avg: formatNumber(Math.round(totals.ok / activeDays)),
            rate: successRate(totals.ok, totals.fail),
          })}
        </div>
      </div>

      <div className="delivery-log-chart-body" onMouseLeave={() => setActive(null)}>
        <div className="delivery-log-chart-axis">
          {ticks.map(v => <span key={v}>{formatNumber(v)}</span>)}
        </div>
        <div className="delivery-log-chart-plot">
          {ticks.map(v => <div key={v} className="delivery-log-chart-grid" style={{ bottom: `${(v / top) * 100}%` }} />)}
          {series.map((d, i) => (
            <button
              type="button"
              key={d.date}
              className={`delivery-log-bar-col${active === i ? ' is-active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onFocus={() => setActive(i)}
              onClick={() => setActive(active === i ? null : i)}
              aria-label={t('barTitle', {
                date: formatDate(parseDay(d.date), { day: 'numeric', month: 'long' }),
                ok: d.success, unassigned: d.unassigned || 0, fail: d.failure,
              })}
            >
              <span className="delivery-log-bar-stack">
                {segments.map(([key, cls]) => (d[key] || 0) > 0 && (
                  <span key={key} className={`delivery-log-bar delivery-log-bar-${cls}`} style={{ height: `${(d[key] / top) * 100}%` }} />
                ))}
              </span>
            </button>
          ))}
          {day && (
            <div
              className="delivery-log-tooltip"
              style={active < series.length / 2
                ? { left: `calc(${((active + 1) / series.length) * 100}% + 8px)` }
                : { right: `calc(${((series.length - active) / series.length) * 100}% + 8px)` }}
            >
              <strong>{formatDate(parseDay(day.date), { weekday: 'long', day: 'numeric', month: 'long' })}</strong>
              {dayTotal(day) === 0 ? (
                <span>{t('tipNone')}</span>
              ) : (
                <>
                  <span><i className="delivery-log-dot delivery-log-dot-success" />{t('tipSuccess', { n: formatNumber(day.success), people: formatNumber(day.senders || 0) })}</span>
                  {(day.unassigned || 0) > 0 && (
                    <span><i className="delivery-log-dot delivery-log-dot-unassigned" />{t('tipUnassigned', { n: formatNumber(day.unassigned) })}</span>
                  )}
                  {day.failure > 0 && (
                    <span><i className="delivery-log-dot delivery-log-dot-failure" />{t('tipFailure', { n: formatNumber(day.failure) })}</span>
                  )}
                  <span className="delivery-log-tooltip-rate">{t('tipRate', { n: successRate(day.success, day.failure) })}</span>
                </>
              )}
            </div>
          )}
        </div>
        <div />
        <div className="delivery-log-chart-labels">
          {series.map((d, i) => (
            <span key={d.date}>
              {i === last ? t('today') : (last - i) % 7 === 0 ? formatDate(parseDay(d.date), { day: 'numeric', month: 'short' }) : ''}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function OutcomeDistribution({ distribution }) {
  useT(messages); // re-render on a language switch (labels come from describeDeliveryOutcome)
  if (!distribution || distribution.length === 0) return null;
  return (
    <div className="delivery-log-distribution">
      {distribution.map(d => (
        <div className="delivery-log-distribution-row" key={d.outcome}>
          <span className="delivery-log-distribution-label">{describeDeliveryOutcome({ outcome: d.outcome }).label === d.outcome ? d.label : describeDeliveryOutcome({ outcome: d.outcome }).label}</span>
          <div className="delivery-log-distribution-bar-track">
            <div className="delivery-log-distribution-bar" style={{ width: `${d.percentage}%` }} />
          </div>
          <span className="delivery-log-distribution-count">{d.count} (%{d.percentage})</span>
        </div>
      ))}
    </div>
  );
}

export default function DeliveryLog() {
  const { t, formatDateTime, formatNumber } = useT(messages);
  useEffect(() => {
    document.title = t('docTitle');
  }, [t]);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const [emailInput, setEmailInput] = useState('');
  const [appliedEmail, setAppliedEmail] = useState('');

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const data = await api.get(endpoints.signatures.deliveryLog.stats);
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setStatsLoading(false);
    }
  };

  const loadLogs = async (targetPage, email) => {
    setLogsLoading(true);
    try {
      const params = { page: targetPage, page_size: PAGE_SIZE };
      if (email) params.email = email;
      const data = await api.get(endpoints.signatures.deliveryLog.list, { params });
      setLogs(data.results || []);
      setCount(data.count || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { loadLogs(page, appliedEmail); }, [page, appliedEmail]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setAppliedEmail(emailInput.trim());
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div className="page-stack">
      <h1>{t('title')}</h1>
      <p className="delivery-log-hint">{t('hint')}</p>

      {error && <Alert variant="error">{t('error', { msg: error })}</Alert>}

      <h3 className="delivery-log-subheading">{t('addinUsage')}</h3>
      <AddinUsage />

      {statsLoading ? (
        <div className="page-loading"><Spinner /></div>
      ) : stats && (
        <>
          <h3 className="delivery-log-subheading">{t('allTime')}</h3>
          <p className="delivery-log-lifetime-hint">{t('allTimeHint')}</p>
          <div className="delivery-log-tiles">
            <DashboardCard CardIcon={<SuccessIcon />} title={t('lifetimeSuccess')} count={formatNumber(stats.lifetime_success)} variant="dark" />
            <DashboardCard CardIcon={<ErrorIcon />} title={t('lifetimeFailure')} count={formatNumber(stats.lifetime_failure)} />
          </div>

          <h3 className="delivery-log-subheading">{t('last30')}</h3>
          <p className="delivery-log-lifetime-hint">{t('last30Hint')}</p>
          <div className="delivery-log-tiles">
            <DashboardCard CardIcon={<DeliveryIcon />} title={t('total30')} count={stats.total} />
            <DashboardCard CardIcon={<SuccessIcon />} title={t('success')} count={stats.success_count} />
            <DashboardCard CardIcon={<UserIcon />} title={t('unassigned')} count={stats.unassigned_count ?? 0} />
            <DashboardCard CardIcon={<ErrorIcon />} title={t('failure')} count={stats.failure_count} />
          </div>
          {stats.client_signature && (
            <>
              <h3 className="delivery-log-subheading">{t('clientSig')}</h3>
              <p className="delivery-log-lifetime-hint">{t('clientSigHint')}</p>
              <div className="delivery-log-tiles">
                <DashboardCard CardIcon={<ErrorIcon />} title={t('clientSigOn')} count={stats.client_signature.enabled} />
                <DashboardCard CardIcon={<SuccessIcon />} title={t('clientSigOff')} count={stats.client_signature.disabled} />
              </div>
            </>
          )}
          <h3 className="delivery-log-subheading">{t('daily')}</h3>
          <DailyDeliveryChart series={stats.daily_series} />
          <h3 className="delivery-log-subheading">{t('distribution')}</h3>
          <OutcomeDistribution distribution={stats.distribution} />
        </>
      )}

      <h3 className="delivery-log-subheading">{t('records')}</h3>
      <form className="delivery-log-search" onSubmit={handleSearchSubmit}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" />
        </svg>
        <input
          type="text"
          placeholder={t('searchPlaceholder')}
          value={emailInput}
          onChange={e => setEmailInput(e.target.value)}
        />
        <Button type="submit" variant="primary" size="md">{t('search')}</Button>
        {appliedEmail && (
          <button type="button" className="delivery-log-search-clear" onClick={() => { setEmailInput(''); setAppliedEmail(''); setPage(1); }}>
            {t('clear')}
          </button>
        )}
      </form>

      {logsLoading ? (
        <div className="page-loading"><Spinner /></div>
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('date')}</th>
                <th>{t('email')}</th>
                <th>{t('client')}</th>
                <th>{t('outcome')}</th>
                <th>{t('groupTemplate')}</th>
                <th>{t('detail')}</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && (
                <tr><td colSpan={6} className="data-table-empty">{t('noRecords')}</td></tr>
              )}
              {logs.map(log => {
                const { label, reason } = describeDeliveryOutcome(log);
                const logStatus = deliveryStatus(log);
                return (
                  <tr key={log.id}>
                    <td>{formatDateTime(log.created_at)}</td>
                    <td>{log.email}</td>
                    <td>{t('clients')[log.client] || log.client || '—'}</td>
                    <td>
                      <Badge variant={{ success: 'success', unassigned: 'warning', failure: 'danger' }[logStatus]}>
                        {logStatus === 'success' ? t('success') : logStatus === 'unassigned' ? t('unassignedBadge') : label}
                      </Badge>
                    </td>
                    <td>{log.matched_group || log.template_name || '—'}</td>
                    <td>{reason || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {count > PAGE_SIZE && (
            <div className="delivery-log-pagination">
              <Button type="button" variant="secondary" size="md" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>{t('prev')}</Button>
              <span>{page} / {totalPages}</span>
              <Button type="button" variant="secondary" size="md" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>{t('next')}</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
